/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file parseLicenceCredits.tsx
 * @desc Created on Wed Apr 30 2025 17:12:53
 * @copyright All rights reserved @ Inarix
 */

import { Chip, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import {
  ExtendedLicense,
  LicenseStatus,
  PackStatus,
} from "../../redux/actions/licenses/licenceTypes";
import { Colors } from "./LicenseFilterBtns";
import { TFunction } from "i18next";

export function remainingCredsPct(li: ExtendedLicense) {
  const currPackCreds =
    (
      li.packs?.find((p) => p.statusConstantId === PackStatus.active) ||
      li.packs?.findLast((p) => p.statusConstantId === PackStatus.emptied)
    )?.initialCredits || 0;
  const nextCredits = li.packs?.reduce(
    (total, p) =>
      (total += p.statusConstantId === PackStatus.bound ? p.initialCredits : 0),
    0,
  );
  const availableCreds = li.availableCredits + nextCredits;
  const totalCreds = currPackCreds + nextCredits;

  return {
    pct: availableCreds / (totalCreds || 1),
    // totalCreds,
    // availableCreds,
    currPackCreds,
    nextCredits,
  };
}

export function licenseIsInfinite(li: ExtendedLicense) {
  return (
    new Date(li.expiryDate).getTime() > Date.now() &&
    li.licenseTemplate.parameters?.defaultScenarioCost === 0 &&
    !li.licenseTemplate.parameters?.specificScenarioCosts
  );
}

export function renderLicenseCredits(
  licenses: ExtendedLicense[],
  t: TFunction<"global", undefined>,
  addTxt?: string,
  expireTxt?: string,
) {
  if (!licenses?.length && addTxt) {
    return <Link to="#">{addTxt}</Link>;
  }

  for (const license of licenses) {
    if (new Date(license.expiryDate).getTime() - Date.now() <= 0) {
      continue;
    }

    if (licenseIsInfinite(license)) {
      // unlimited license
      return <span>∞</span>;
    }

    if (
      license.statusConstantId <= LicenseStatus.emptied ||
      new Date(license.expiryDate).getTime() > Date.now()
    ) {
      const oneMonthFromNow = new Date();
      const { pct, currPackCreds } = remainingCredsPct(license);

      const color = pct <= 0.2 ? Colors.orange : Colors.green;
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      return (
        <>
          <Tooltip title={pct <= 0.2 ? t("licenses.lowOrEmpty_desc") : ""}>
            <Chip
              style={{
                height: 10,
                width: 10,
                marginRight: 5,
                backgroundColor: color,
              }}
              variant="filled"
            />
          </Tooltip>
          &nbsp;&nbsp;
          <span>{`${license.availableCredits} / ${currPackCreds}`}</span>
        </>
      );
    }
  }

  return <Chip label={expireTxt} variant="outlined" />;
}

export function renderLicenseExpiration(
  licenses: ExtendedLicense[],
  t: TFunction<"global", undefined>,
  addTxt?: string,
  expireTxt?: string,
) {
  if (!licenses?.length && addTxt) {
    return <Link to="#">{addTxt}</Link>;
  }

  for (const license of licenses) {
    if (license.statusConstantId === LicenseStatus.expired) {
      continue;
    }

    if (licenseIsInfinite(license)) {
      // unlimited license
      return <span>∞</span>;
    }

    if (license.statusConstantId <= LicenseStatus.emptied) {
      const oneMonthFromNow = new Date();
      const { currPackCreds } = remainingCredsPct(license);

      return (
        <>
          {license.expiryDate &&
            new Date(license.expiryDate) < oneMonthFromNow && (
              <Tooltip title={t("licenses.expSoon_desc")}>
                <Chip
                  style={{
                    height: 10,
                    width: 10,
                    marginRight: 5,
                    backgroundColor: Colors.red,
                  }}
                  variant="filled"
                />
              </Tooltip>
            )}
          &nbsp;&nbsp;
          <span>{`${license.availableCredits} / ${currPackCreds}`}</span>
        </>
      );
    }
  }

  return <Chip label={expireTxt} variant="outlined" />;
}

export function renderLicenseExtrapack(
  licenses: ExtendedLicense[],
  addTxt?: string,
  expireTxt?: string,
) {
  if (!licenses?.length && addTxt) {
    return <Link to="#">{addTxt}</Link>;
  }

  for (const license of licenses) {
    if (new Date(license.expiryDate).getTime() - Date.now() <= 0) {
      continue;
    }

    if (licenseIsInfinite(license)) {
      // unlimited license
      return <span>∞</span>;
    }

    if (
      license.statusConstantId <= LicenseStatus.emptied ||
      new Date(license.expiryDate).getTime() > Date.now()
    ) {
      const { nextCredits } = remainingCredsPct(license);

      return (
        <>
          &nbsp;&nbsp;
          <span>{`${nextCredits ? ` +${nextCredits}` : "-"}`}</span>
        </>
      );
    }
  }

  return <Chip label={expireTxt} variant="outlined" />;
}
